import { observer } from "mobx-react-lite";
import { Accordion, Grid, Icon, Label, List, Message, Segment } from "semantic-ui-react";
import React, { useState } from "react";
import styles from "../../../../Assessment/AssessmentInfoSlideControls/LearnMoreTemplate.module.css"

const cdnRoute = process.env.REACT_APP_CDN_ROUTE + "/medical/";

interface TaskModalProps {
  learnMoreTitle: string;
}

const LearnMoreModal: React.FC<TaskModalProps> = (props) => {
  const { learnMoreTitle } = props;
  const [activeAccordionIndexes] = useState([0,1,2]);
  const [activeRightAccordionIndexes] = useState([0]);

  const renderAccordionDescription = () => {
    return (
      <>
      <Segment raised className={styles.segmentWhiteBG} >  
        <p className={styles.learnMoreAccordionText} >
        This assessment is a tool used by healthcare practitioners to evaluate an older adult's physical and mental health and to develop a plan for their care.
          <br/><br/>
          The findings may indicate that plans need to be made to ensure the older adult's safety and well-being, such as making new housing arrangements or getting in-home assistance.
        </p>
      </Segment>
      </>
    );
  };
  const renderAccordionBenefits = () => {
    return (
      <>
      <Segment raised className={styles.segmentWhiteBG} >  
         <List divided relaxed  className={styles.learnMoreBenefitsList}>
          <>
            <List.Item>
              <List.Icon name="check" size="large" color='green' verticalAlign="middle" />
              <List.Content description="To determine functional status" />
            </List.Item>
            <List.Item>
            <List.Icon name="check" size="large" color='green' verticalAlign="middle" />
              <List.Content description="To aid in the development of a long-term plan" />
            </List.Item>
            <List.Item>
              <List.Icon name="check" size="large" color='green' verticalAlign="middle" />
              <List.Content description="To provide baseline information for assessing any change in future health" />
            </List.Item>
          </>
        </List>
      </Segment>
      </>
    );
  };
  const renderAccordionExpectations = () => {
    return (
      <>
      <Segment raised className={styles.segmentWhiteBG} >  
        <List divided relaxed className={styles.learnMoreBulletedList} bulleted >
          <>
            <List.Item description="Psychosocial assessment" />
            <List.Item description="Medication review" />
            <List.Item description="Comprehensive physical exam" />
            <List.Item description="Mental status exam" />
            <List.Item description="Environmental assessment" />
            <List.Item description="Housing adaptations and barriers" />
            <List.Item description="Mobility" />
            <List.Item description="Spiritual assessment" />
            <List.Item description="Functional assessment" style={{bottomBorder: "none"}}/>
          </>
        </List>
      </Segment>
      </>
    );
  };
  const renderAccordionPreparing = () => {
    return (
      <>   
      <Segment raised className={styles.segmentWhiteBG} >  
        <p className={styles.learnMoreAccordionText} >
          Prior to the assessment, a family member or caregiver should
          help track past and current major illnesses, conditions, and surgeries - including 
          information on allergies and adverse medication reactions.
          <br/><br/>
          Equally important to medical history is the compiling and upkeeping of a list of symptoms, issues, complaints, and
          improvements from current or past treatments. Helping your loved one keep this information current will ease the stress of 
          pre-evaluation information gathering and will give the medical professionals a more thorough and comprehensive picture.
          <br/><br/>
          <span className={styles.learnMoreAccordionTextEmphasis}>
            If possible, you, or another caregiver, should attend the appointment.
          </span>
        </p>
      </Segment>
      </>
    );
  };

  const accordions = [
    { key: 'description',
      index: 0,
      title: { 
        icon: <Icon name='dropdown' className={styles.learnMoreAccordionLabelTitleIcon} />, 
        content: <Label className={styles.learnMoreAccordionLabelTitle} > What is a {learnMoreTitle}? </Label> },
      content: { 
        content: (<Message className={styles.learnMoreAccordionMessageBox} content={renderAccordionDescription()}/>)
      }
    },
    { key: 'benefits',
      index: 1,
      title: { 
        icon: <Icon name='dropdown' className={styles.learnMoreAccordionLabelTitleIcon} />, 
        content: <Label className={styles.learnMoreAccordionLabelTitle} > Benefits of the Assessment: </Label> },
      content: { 
        content: (<Message className={styles.learnMoreAccordionMessageBox} content={renderAccordionBenefits()}/>)
      }
    },
    { key: 'preparing',
      index: 2,
      title: { 
        icon: <Icon name='dropdown' className={styles.learnMoreAccordionLabelTitleIcon} />, 
        content: <Label className={styles.learnMoreAccordionLabelTitle} > Preparing for the Assessment: </Label>},
      content: { 
        content: (<Message className={styles.learnMoreAccordionMessageBox} content={renderAccordionPreparing()}/>)
      }
    }
  ];
  const accordionsRight = [
    {  key: 'expectations',
      index: 0,
      title: { 
        icon: <Icon name='dropdown' className={styles.learnMoreAccordionLabelTitleIcon} />, 
        content: <Label className={styles.learnMoreAccordionLabelTitle} > What to Expect: </Label>},
      content: { 
        content: (<Message className={styles.learnMoreAccordionMessageBox} content={renderAccordionExpectations()}/>)
      }
    }
  ];

  return (
    <>
    <Segment className={styles.learnMoreBackgroundImg} style = {{ backgroundImage: `url(${cdnRoute}GeriatricInfoPageOne.png)`, }}>
      <div className={styles.learnMoreModalContent}>
        <Grid stackable doubling padded columns='equal'>
          <Grid.Column width={8} id={styles['learn-more-leftside']}>
            <Accordion  panels={accordions} className={styles.learnMoreModalAccordionGroup} defaultActiveIndex={activeAccordionIndexes} exclusive={false} />
          </Grid.Column>

          <Grid.Column id={styles['learn-more-rightside']}>
            <div style={{width: '100%', height: 'auto', paddingBottom: '2em'}}>
              {/* quote1 */}
              <div className={styles.learnMoreQuoteBox}>
                <Icon name="quote left" className={styles.learnMoreLeftQuotationMark} />
                <span className={styles.learnMoreQuoteBoxSpan}>
                  <p className={styles.learnMoreQuoteText} > 
                    “An assessment could lead to solving problems and helping a parent remain independent longer.”</p>
                  <p className={styles.learnMoreQuoteSourceText} >(AARP, 2003)</p>
                </span>
                <Icon name="quote right"  className={styles.learnMoreRightQuotationMark} />
              </div>
              {/* quote2 */}
              <div className={styles.learnMoreQuoteBox}>
                <Icon name="quote left" className={styles.learnMoreLeftQuotationMark} />
                <span className={styles.learnMoreQuoteBoxSpan}>
                  <p className={styles.learnMoreQuoteText} > “A good medical care plan can result in fewer accidents or
                    falls, less illness, a longer life, more quality of life, and greater independence.”</p>
                  <p className={styles.learnMoreQuoteSourceText} >(AARP, 2003)</p>
                </span>
                <Icon name="quote right"  className={styles.learnMoreRightQuotationMark} />
              </div>
            </div>
            <Accordion panels={accordionsRight} className={styles.learnMoreModalAccordionGroup} defaultActiveIndex={activeRightAccordionIndexes} exclusive={false} />
          </Grid.Column>
        </Grid>
      </div>
    </Segment>  
      
    </>
  );
};

export default observer(LearnMoreModal);
